import { Injectable, Inject } from "@angular/core";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { defer, lastValueFrom, Observable, retry, throwError, timer } from "rxjs";
import { map } from "rxjs/operators";
import { SharedService } from "./shared.service";
import { ConfigurationService } from "./configuration.service";
import { IVisibleFor } from "../model/dialog-state.model";
import { ErrorService } from "./error.service";
import { MonitoringService } from "./monitoring.service";
import { OutlookService } from "./outlook/outlook.service";
import { PreferenceService } from "./preference.service";

@Injectable({
  providedIn: "root",
})
export class ComposeService {

  bcc: string = null;
  bccObj: Office.EmailAddressDetails = null;

  constructor(
    @Inject("BASE_URL") private readonly baseUrl: string,
    private readonly httpClient: HttpClient,
    private readonly configService: ConfigurationService,
    private readonly sharedService: SharedService,
    private readonly errorService: ErrorService,
    private readonly monitoringService: MonitoringService,
    private readonly outlookService: OutlookService,
    private readonly prefs: PreferenceService
  ) {
    this.bcc = this.configService.config.bcc;
    this.bccObj = {
      emailAddress: this.bcc,
      displayName: "SuperOffice Mail Archive",
      recipientType: "externalUser",
      appointmentResponse: null,
    };
  }

  public loadCompose() {
    console.log("Loading Compose Services");
  }

  /**
   * Get Recipients from TO
   */
  getTO(): Promise<Office.EmailAddressDetails[]> {
    return lastValueFrom(this.outlookService.to);
  }

  /**
   * Get Recipients from CC
   */
  getCC(): Promise<Office.EmailAddressDetails[]> {
    return lastValueFrom(this.outlookService.cc);
  }

  /**
   * Get Recipients from BCC
   */
  getBCC(): Promise<Office.EmailAddressDetails[]> {
    const bccOut: Office.EmailAddressDetails = {
      emailAddress: this.configService.config.bcc,
      displayName: "",
      appointmentResponse: null,
      recipientType: "externalUser",
    };

    return lastValueFrom(this.outlookService.bcc.pipe(map(res => {
        const unique = (res as Office.EmailAddressDetails[]).filter(
          (item, index, arr) => arr.findIndex(i => i.emailAddress === item.emailAddress) === index
        );
        const bccs = unique.filter(item => item.emailAddress !== bccOut.emailAddress);
        return bccs;
    })));
  }

  /**
   * Get BCC Recipients with archive mail id
   */
  async getAllEmailBCCs(): Promise<string[]> {
    return new Promise<string[]>((resolve, reject) => {
      lastValueFrom(this.outlookService.bcc.pipe(map(res => {
        const bccs = res.map((i) => i.emailAddress);
        resolve([...new Set(bccs)]);
      })))
    });
  }

  async setArchiveBCC(on: boolean): Promise<void> {
    const bccs = await this.getBCC();
    const filtered = bccs.filter(item => item.emailAddress !== this.bccObj.emailAddress);

    if (on) {
      filtered.push(this.bccObj);
    }
    await this.outlookService.setRecipient("bcc", filtered);
  }

  addRecipients(type: string, recipients: Office.EmailAddressDetails[]): Promise<void> {
    return this.outlookService.addRecipient(type, recipients);
  }

  /**
   * Get All Recipients - Which includes individual observables 'TO, CC & BCC'
   */
  async getRecipients(): Promise<Office.EmailAddressDetails[]> {
    const results = await Promise.all([
      this.getTO(),
      this.getCC(),
      this.getBCC()
    ]);
    return [...results[0], ...results[1], ...results[2]].filter(
      (item, index, arr) => arr.findIndex(i => i.emailAddress === item.emailAddress) === index
    );
  }

  // Kept small: repeated saveAsync calls are unverified against the Exchange 9042 risk noted in
  // ComposeAppointmentComponent.loadExtension. Revisit once tested on real Outlook clients.
  private static readonly ITEM_ID_MAX_ATTEMPTS = 3;
  private static readonly ITEM_ID_RETRY_DELAY_MS = 400;

  /** Can still reject: immediately if saveAsync fails outright, or after exhausting retries. */
  getOfficeItemID(): Promise<string> {
    return this.resolveOfficeItemID(1);
  }

  private async resolveOfficeItemID(attempt: number): Promise<string> {
    if (attempt > 1 && this.outlookService.itemId) {
      const cachedItemId = this.sharedService.convertEwsID(this.outlookService.itemId);
      if (cachedItemId) {
        return cachedItemId;
      }
    }

    const itemId = await new Promise<string>((resolve, reject) => {
      this.outlookService.mailboxItem.saveAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve(this.sharedService.convertEwsID(result.value));
        } else {
          this.monitoringService.logCustomEvent("ERROR: getOfficeItemID saveAsync failed", {
            attempt,
            status: result.status,
            error: String(result.error?.message ?? result.error)
          });
          reject(result.error);
        }
      });
    });

    if (itemId) {
      return itemId;
    }

    // saveAsync can succeed with an id that converts to empty/null while Outlook is still finalizing the item.
    if (attempt >= ComposeService.ITEM_ID_MAX_ATTEMPTS) {
      this.monitoringService.logCustomEvent("ERROR: getOfficeItemID exhausted retries with empty item id", { attempts: attempt });
      throw new Error("Unable to resolve appointment item id after retries");
    }

    this.monitoringService.logCustomEvent("WARN: getOfficeItemID got empty item id, retrying", { attempt });
    await new Promise<void>(resolve => setTimeout(resolve, ComposeService.ITEM_ID_RETRY_DELAY_MS));
    return this.resolveOfficeItemID(attempt + 1);
  }

  private static readonly TRANSIENT_STATUSES = new Set<number>([0, 408, 429, 503, 504]);
  // For archive submission only (getOfficeItemID above has a separate, unrelated retry) —
  // StoreArchiveInfo makes exactly one attempt server-side.
  private static readonly MAX_RETRY_COUNT = 2;
  // Inert at the current MAX_RETRY_COUNT (attempt 2's 2s delay never reaches this); matters if
  // the count is raised.
  private static readonly MAX_RETRY_DELAY_MS = 4_000;
  private static readonly RETRY_JITTER_MS = 250;

  // Cold Observable — the HTTP call fires only on subscription, so `retry` genuinely redoes it.
  private archiveRequest$(sendArchive: INonce): Observable<void> {
    return defer(() => {
      const url = this.baseUrl + "api/sendandarchive";
      return this.httpClient.post<void>(url, {
        uniqueId: sendArchive.uniqueId,
        company: sendArchive.company,
        contact: sendArchive.contact,
        project: sendArchive.project,
        sale: sendArchive.sale,
        type: sendArchive.type,
        visibleFor: sendArchive.visibleFor,
        isCompleted: sendArchive.isCompleted,
        revision: sendArchive.revision,
        categoryTaggingEnabled: this.prefs.isCategoryTaggingOnSendAndArchiveEnabled
      });
    });
  }

  // Retried with capped backoff + jitter on transient failures. An Observable, not a Promise —
  // a caller using `switchMap` gets in-flight cancellation (request + pending retry) for free.
  postArchive$(sendArchive: INonce): Observable<void> {
    return this.archiveRequest$(sendArchive).pipe(
      retry({
        count: ComposeService.MAX_RETRY_COUNT,
        // RxJS types this `any`; `unknown` is honest about that — instanceof below narrows it.
        delay: (error: unknown, attempt) => {
          if (error instanceof HttpErrorResponse && ComposeService.TRANSIENT_STATUSES.has(error.status)) {
            const delayMs = this.getRetryDelayMs(attempt);
            this.monitoringService.logTrace(`postArchive transient ${error.status} - retry ${attempt}/${ComposeService.MAX_RETRY_COUNT} in ${delayMs}ms`);
            return timer(delayMs);
          }
          return throwError(() => error);
        }
      })
    );
  }

  private getRetryDelayMs(attempt: number): number {
    const baseDelayMs = Math.pow(2, attempt - 1) * 1000; // 1s, 2s
    const cappedMs = Math.min(baseDelayMs, ComposeService.MAX_RETRY_DELAY_MS);
    return cappedMs + Math.floor(Math.random() * ComposeService.RETRY_JITTER_MS);
  }

  // 128 bits of random hex, not derived from tenant data — matches ArchiveTokenPattern server-side.
  generateArchiveToken(): string {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, b => b.toString(16).padStart(2, "0")).join("");
  }

  getArchiveTokenFromHeader(): Promise<string | null> {
    return new Promise<string | null>((resolve) => {
      this.sharedService.officeMailbox.item.internetHeaders.getAsync(
        ["X-SuperOffice-Nonce"],
        {asyncContext: null},
        (result: Office.AsyncResult<Record<string, string> | null>) => {
          try {
            if (result.status !== Office.AsyncResultStatus.Succeeded) {
              resolve(null);
              return;
            }
            // Null, not {}, on classic Outlook LTSC 2021 when the header is missing.
            resolve(result.value?.["X-SuperOffice-Nonce"] ?? null);
          } catch (error) {
            // Office.js swallows a throw from this callback, which would leave the promise pending forever.
            this.monitoringService.logTrace("getArchiveTokenFromHeader callback failed", { error: String(error) });
            resolve(null);
          }
        }
      );
    });
  }

  // Set before postArchive$ resolves, so a fast Send can't beat the POST and ship with no header.
  async setHeaderImmediately(token: string, traceId: string, spanId: string): Promise<boolean> {
    const success = await this.setComposeInternetHeader(token, traceId, spanId);
    this.monitoringService.logTrace("setHeaderImmediately", {
      SOUniqueId: token,
      SOHeaderSetSuccess: success
    });
    return success;
  }

  /**
   * Delete the entry in the DB
   *
   * @param uniqueId The archive token (also the Cosmos document id)
   */
  async deleteNonce(uniqueId: string): Promise<void> {
    const url = this.baseUrl + "api/sendandarchive/" + uniqueId;
    await this.httpClient.delete<string>(url).toPromise();
  }

  /**
   * Set the mail header
   *
   * @param token The archive token
   */
  setHeader(token: string, traceId: string, spanId: string): Promise<boolean> {
    return this.setComposeInternetHeader(token, traceId, spanId);
  }

  /**
   * Set the mail header using Office API
   *
   * @param token The archive token
   */
  setComposeInternetHeader(token: string, traceId: string, spanId: string): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.sharedService.officeMailbox.item.internetHeaders.setAsync(
        {
          "X-SuperOffice-Nonce": token,
          "X-SuperOffice-Trace-Id": traceId,
          "X-SuperOffice-Span-Id": spanId
        },
        {asyncContext: null},
        (result: Office.AsyncResult<void>) => {
          const success = result.status === Office.AsyncResultStatus.Succeeded;
          if (!success) {
            console.error("composeService.setComposeInternetHeader", result.error);
          }
          resolve(success);
        }
      );
    });
  }

  handleComposeError(error: any) {
    this.errorService.handleError(error);
  }
}

export interface INonce {
  tenant: string;
  uniqueId: string;
  mailId: string;
  crmUserId: string;
  bcc: string;
  company: number;
  contact: number;
  project: number;
  sale: number;
  type: number;
  visibleFor: IVisibleFor;
  isCompleted: boolean;
  // A monotonic counter, not a timestamp — see ComposeArchiveComponent.nextRevision(). Lets the
  // server reject a write older than what's already stored.
  revision: number;
}
